/**
 * composeRefs.ts
 *
 * Hook for forwarding a DOM node to multiple refs at once. Used where a single
 * element needs to be tracked by both a parent's forwarded ref and one or more
 * local refs (e.g. measurement + keyboard scope on the same `<div>`).
 *
 * Supports React 19 callback-ref cleanups: if any of the supplied refs returns
 * a cleanup function, the composed ref returns one that calls every cleanup
 * (and clears any non-cleanup refs to null) on unmount.
 *
 * In-repo replacement for `@radix-ui/react-compose-refs`.
 */

import { useCallback } from "react";
import type { Ref, RefCallback } from "react";

type PossibleRef<T> = Ref<T> | undefined;

function setRef<T>(ref: PossibleRef<T>, value: T | null): void | (() => void) {
  if (typeof ref === "function") {
    return ref(value) as void | (() => void);
  }
  if (ref !== null && ref !== undefined) {
    (ref as { current: T | null }).current = value;
  }
  return undefined;
}

function composeRefs<T>(...refs: PossibleRef<T>[]): RefCallback<T> {
  return (node) => {
    let hasCleanup = false;
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node);
      if (!hasCleanup && typeof cleanup === "function") {
        hasCleanup = true;
      }
      return cleanup;
    });
    if (hasCleanup) {
      return () => {
        for (let i = 0; i < cleanups.length; i++) {
          const cleanup = cleanups[i];
          if (typeof cleanup === "function") {
            cleanup();
          } else {
            setRef(refs[i], null);
          }
        }
      };
    }
    return undefined;
  };
}

export function useComposedRefs<T>(...refs: PossibleRef<T>[]): RefCallback<T> {
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- spread refs are the deps
  return useCallback(composeRefs(...refs), refs);
}
